import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import Header from "../../../components/Header";
import { accountsmanagement } from "../../../config";
import {
  graphqlQuery,
  generateAndExecuteMutation,
  fetchDataEngine,
} from "../../../data/Axios/DynamicService";
import {
  allAccountSubCategories,
  allAccounts,
} from "../../../data/Axios/queries";
import { filterAccountSubCategoriesByUseCase } from "../../../data/finance/accountCategoryRules";

const base_url = accountsmanagement.uri;

const PurchaseInvoiceLinesForm = (props) => {
  const { isEditing = false, data = {}, onClose, refetchData } = props;

  // formatting helper for amounts
  const formatAmount = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const str = val.toString().replace(/,/g, "");
    if (str === "" || isNaN(str)) return "";
    const parts = str.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  // 1. Read the parent ID from localStorage (mirroring ProformaLinesForm)
  const invoiceData = JSON.parse(localStorage.getItem("MyPurchaseInvoiceData") || "{}");
  const parentPurchaseInvoiceId = invoiceData?.id || null; // <--- NEW

  const [initialValues, setInitialValues] = useState({
    id: data?.id || null,
    // 2. Add purchaseInvoiceId to initial values
    purchaseInvoiceId: data?.purchaseInvoiceId || parentPurchaseInvoiceId || "", // <--- NEW
    accountSubCategoryId: data?.accountTypeId || "",
    accountId: data?.accountId || "",
    description: data?.description || "",
    amount: data?.amount != null ? formatAmount(data.amount) : "",
    isActive: data?.isActive || 1,
  });

  const [accountSubCategories, setAccountSubCategories] = useState([]);
  const [allAccountsData, setAllAccountsData] = useState([]);
  // const [filteredAccounts, setFilteredAccounts] = useState([]); // Removed as it's not used

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  // Fetch dropdown data
 useEffect(() => {
  const fetchData = async () => {
    try {
      const subCategoryData = await graphqlQuery(allAccountSubCategories, base_url);

      const filteredSubCategories = filterAccountSubCategoriesByUseCase(
        subCategoryData || [],
        "PURCHASE_INVOICE_LINE"
      );

      setAccountSubCategories(filteredSubCategories);
    } catch (error) {
      console.error("Error fetching account subcategories:", error);
    }
  };

  fetchData();
}, []);


  const handleAccountTypeChange = async (event) => {
      const selectedAccountTypeId = event.target.value;
      try {
        const paymentAccounts = await fetchDataEngine(
          'allAccountsByColumnValues',
          'accounts',
          { accountSubcategoryId: selectedAccountTypeId },
          ['id', 'accountNumber', 'accountName'],
          base_url,
          
        );
        if (paymentAccounts !== null) {
          setAllAccountsData(paymentAccounts);
        }
      } catch (error) {
        console.error("Error fetching payment accounts:", error);
      }
    };

  // Set initial values based on parent ID if not editing
  // Similar to the useEffect in ProformaLinesForm
  useEffect(() => {
    if (!isEditing && parentPurchaseInvoiceId) {
      setInitialValues((prev) => ({
        ...prev,
        purchaseInvoiceId: parentPurchaseInvoiceId,
      }));
    }
    // Also handle mapping when editing, though your original 'initialValues' covers 'data'
    if (isEditing && data) {
         setInitialValues((prev) => ({
            ...prev,
            purchaseInvoiceId: data.purchaseInvoiceId || parentPurchaseInvoiceId || "",
            amount: data.amount != null ? formatAmount(data.amount) : "",
         }));
    }
  }, [isEditing, data, parentPurchaseInvoiceId]);


  // Validation schema
  const validationSchema = yup.object().shape({
    accountSubCategoryId: yup.string().required("Account Type is required"),
    accountId: yup.string().required("Account Name is required"),
    description: yup.string().required("Description is required"),
    amount: yup
      .string()
      .required("Amount is required")
      .test("valid-number", "Amount must be greater than zero", (val) => {
        if (val == null) return false;
        const num = parseFloat(val.toString().replace(/,/g, ""));
        return !isNaN(num) && num > 0;
      }),
    // You might want to validate the parent ID if you render the form outside of the main list view:
    // purchaseInvoiceId: yup.string().required("Parent Invoice ID is missing"),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = {
        ...values,
        // 3. Ensure purchaseInvoiceId is parsed as an integer
        purchaseInvoiceId: parseInt(parentPurchaseInvoiceId || values.purchaseInvoiceId), // <--- NEW
        accountTypeId: parseInt(values.accountSubCategoryId),
        accountId: parseInt(values.accountId),
        createdBy: userId,
      };

      // amount may contain commas; convert to number before sending
      if (mutationData.amount != null) {
        const raw = mutationData.amount.toString().replace(/,/g, "");
        mutationData.amount = parseFloat(raw) || 0;
      }

      // Critical cleanup from previous debugging steps
      delete mutationData.accountSubCategoryId;
      
      // Remove 'id' if creating and it's null (GraphQL best practice)
      if (!isEditing && mutationData.id === null) {
          delete mutationData.id;
      }

      const mutationName = isEditing
        ? "updatePurchaseInvoiceLine"
        : "createPurchaseInvoiceLine";
      const responseKey = isEditing
        ? "updatePurchaseInvoiceLine"
        : "newPurchaseInvoiceLine";

      const response = await generateAndExecuteMutation(
        mutationName,
        mutationData,
        responseKey,
        base_url
      );

      if (response) {
        onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Purchase Invoice Line ${
            isEditing ? "updated" : "created"
          } successfully.`,
        }).then(() => {
          refetchData();
          
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      onClose();
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong while saving the purchase invoice line.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      {/* ... (Header and Formik setup remain the same) ... */}
      <Header
        title={
          isEditing ? "Edit Purchase Invoice Line" : "Create Purchase Invoice Line"
        }
        subtitle={
          isEditing
            ? "Edit existing purchase invoice line"
            : "Create a new purchase invoice line"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* NOTE: purchaseInvoiceId is included in values but hidden from the UI,
                   as it's derived from the parent context. */}

              {/* Account Type/SubCategory Field */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Account Type *</InputLabel>
                <Select
                  name="accountSubCategoryId"
                  value={values.accountSubCategoryId}
                  onChange={(e) => {
                  handleChange(e);
                  handleAccountTypeChange(e); // Custom handler to fetch accounts
                }}
                  // Custom handler to fetch accounts
                  onBlur={handleBlur}
                  error={touched.accountSubCategoryId && !!errors.accountSubCategoryId}
                >
                  {accountSubCategories.map((subCat) => (
                    <MenuItem key={subCat.id} value={subCat.id}>
                      {subCat.accountSubCategoryName || subCat.accountCategoryName}
                    </MenuItem>
                  ))}
                </Select>
                {touched.accountSubCategoryId && errors.accountSubCategoryId && (
                  <Typography color="error">{errors.accountSubCategoryId}</Typography>
                )}
              </FormControl>

              {/* Account Name Field */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Account Name *</InputLabel>
                <Select
                  name="accountId"
                  value={values.accountId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountId && !!errors.accountId}
                >
                  {allAccountsData.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.accountName}
                    </MenuItem>
                  ))}
                </Select>
                {touched.accountId && errors.accountId && (
                  <Typography color="error">{errors.accountId}</Typography>
                )}
              </FormControl>

              {/* Description */}
              <TextField
                fullWidth
                variant="filled"
                label="Description *"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
              />

              {/* Amount */}
              <TextField
                fullWidth
                variant="filled"
                label="Amount *"
                name="amount"
                // use text so we can insert commas
                inputMode="decimal"
                value={values.amount}
                onChange={(e) => {
                  // allow only digits and period, and keep formatting
                  const raw = e.target.value.replace(/,/g, "");
                  if (raw === "" || /^[0-9]*\.?[0-9]*$/.test(raw)) {
                    const parts = raw.split(".");
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    setFieldValue("amount", parts.join("."));
                  }
                }}
                onBlur={handleBlur}
                error={touched.amount && !!errors.amount}
                helperText={touched.amount && errors.amount}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Submitting..."
                  : isEditing
                  ? "Update"
                  : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PurchaseInvoiceLinesForm;
